import { RefObject, useEffect, useState } from 'react';

type UseMessageWidthParams = {
    messageRef: RefObject<HTMLElement>;
    minWidthForInsideButton: number;
    enabled?: boolean;
};

/**
 * Hook to determine whether message action buttons (edit, delete) should be displayed
 * inside or outside the message bubble in the discussion thread.
 *
 * Uses ResizeObserver to measure the message width and returns true if there's enough
 * space to display the buttons inside the message bubble, false otherwise.
 */
export const useActionButtonsCouldBeInside = ({
    messageRef,
    minWidthForInsideButton,
    enabled = true,
}: UseMessageWidthParams): boolean => {
    const [areActionButtonsInside, setAreActionButtonsInside] = useState<boolean>(true);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const element = messageRef.current;
        if (!element) {
            return;
        }

        const observer = new ResizeObserver(() => {
            if (element.offsetWidth === 0) {
                return;
            }

            setAreActionButtonsInside(element.offsetWidth > minWidthForInsideButton);
        });

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [messageRef, minWidthForInsideButton, enabled]);

    return areActionButtonsInside;
};
